import { useEffect, useState, useRef } from 'react';
import './App.css';

const ANALYTICS_API_URL = 'https://newtonflaskserver-hfgkcrewf4fgedc6.westus-01.azurewebsites.net';
// const ANALYTICS_API_URL = 'http://localhost:5002';

const fetchFromAPI = async (endpoint, options = {}) => {
  const token = localStorage.getItem('jwtToken');
  const headers = {
    'Content-Type': 'application/json',
    'ngrok-skip-browser-warning': 'true',
    ...options.headers,
  };

  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }

  const response = await fetch(`${ANALYTICS_API_URL}${endpoint}`, {
    ...options,
    headers,
  });

  if (response.status === 401) {
    localStorage.removeItem('jwtToken');
    window.location.reload();
    throw new Error('Session expired, please log in again.');
  }

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({ message: 'An unknown error occurred' }));
    throw new Error(errorData.message);
  }

  return response.json();
};

const CallInsights = () => {
  // State for practices list
  const [practices, setPractices] = useState([]);
  const [practicesLoading, setPracticesLoading] = useState(false);
  const [practicesError, setPracticesError] = useState('');
  
  // State for insights data
  const [insightsData, setInsightsData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  
  // State for AI analysis data
  const [analysisData, setAnalysisData] = useState(null);
  const [analysisLoading, setAnalysisLoading] = useState(false);
  const [analysisError, setAnalysisError] = useState('');
  const [analysisMode, setAnalysisMode] = useState('judge'); // 'judge' or 'transcripts'
  
  // Form states
  const [practiceId, setPracticeId] = useState('ALL');
  const [startDate, setStartDate] = useState(() => {
    // Set to 7 days ago as default (YYYY-MM-DD format)
    const date = new Date();
    date.setDate(date.getDate() - 7);
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  });
  const [endDate, setEndDate] = useState(() => {
    // Set current local date as default (YYYY-MM-DD format)
    const today = new Date();
    const year = today.getFullYear();
    const month = String(today.getMonth() + 1).padStart(2, '0');
    const day = String(today.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  });
  
  // Date range picker UI states
  const [showDatePicker, setShowDatePicker] = useState(false);
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [selectingStart, setSelectingStart] = useState(true);
  const [hoveredDate, setHoveredDate] = useState(null);
  
  // Ref for click-outside functionality
  const datePickerRef = useRef(null);

  // Fetch practices on component mount
  useEffect(() => {
    const fetchPractices = async () => {
      setPracticesLoading(true);
      setPracticesError('');
      
      try {
        const response = await fetchFromAPI('/api/practices/live');
        
        if (response.success && response.data) {
          setPractices(response.data);
          console.log(`Loaded ${response.data.length} live practices`);
        } else {
          setPracticesError('Failed to load practices');
        }
      } catch (error) {
        console.error('Error fetching practices:', error);
        setPracticesError(`Failed to load practices: ${error.message}`);
      } finally {
        setPracticesLoading(false);
      }
    };
    
    fetchPractices();
  }, []);

  // Click-outside handler for date picker
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (datePickerRef.current && !datePickerRef.current.contains(event.target)) {
        setShowDatePicker(false);
      }
    };

    if (showDatePicker) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showDatePicker]);

  // Fetch insights data
  const fetchInsightsData = async () => {
    if (!startDate || !endDate) {
      setError('Please select start and end dates.');
      return;
    }
    
    if (new Date(startDate) > new Date(endDate)) {
      setError('Start date cannot be after end date.');
      return;
    }
    
    setLoading(true);
    setError('');
    
    try {
      const params = new URLSearchParams({
        start_date: startDate,
        end_date: endDate
      });
      
      // Only add practice_id if not "ALL"
      if (practiceId && practiceId !== 'ALL') {
        params.append('practice_id', practiceId);
      }
      
      const response = await fetchFromAPI(`/api/call/insights?${params.toString()}`);
      
      if (response.success && response.data) {
        setInsightsData(response);
        console.log(`Loaded ${response.count} calls for insights`);
        // Clear previous analysis data when fetching new insights
        setAnalysisData(null);
        setAnalysisError('');
      } else {
        setError('No call data found for the selected date range.');
        setInsightsData(null);
        setAnalysisData(null);
      }
    } catch (error) {
      console.error('Error fetching insights data:', error);
      setError(`Failed to load insights data: ${error.message}`);
      setInsightsData(null);
      setAnalysisData(null);
    } finally {
      setLoading(false);
    }
  };

  // Fetch AI analysis data
  const fetchAIAnalysis = async () => {
    if (!insightsData) {
      setAnalysisError('Please fetch insights data first.');
      return;
    }
    
    setAnalysisLoading(true);
    setAnalysisError('');
    
    try {
      const params = new URLSearchParams({
        start_date: startDate,
        end_date: endDate,
        mode: analysisMode
      });
      
      // Only add practice_id if not "ALL"
      if (practiceId && practiceId !== 'ALL') {
        params.append('practice_id', practiceId);
      }
      
      const response = await fetchFromAPI(`/api/call/insights/analysis?${params.toString()}`);
      
      if (response.success && response.data) {
        setAnalysisData(response.data);
        console.log(`Loaded AI analysis (${analysisMode} mode) for ${response.data.total_calls_analyzed} calls`);
      } else {
        setAnalysisError('No analysis data found.');
        setAnalysisData(null);
      }
    } catch (error) {
      console.error('Error fetching AI analysis:', error);
      setAnalysisError(`Failed to load AI analysis: ${error.message}`);
      setAnalysisData(null);
    } finally {
      setAnalysisLoading(false);
    }
  };

  // Clear data
  const clearData = () => {
    setPracticeId('ALL');
    
    // Reset to last 7 days
    const endDateObj = new Date();
    const endYear = endDateObj.getFullYear();
    const endMonth = String(endDateObj.getMonth() + 1).padStart(2, '0');
    const endDay = String(endDateObj.getDate()).padStart(2, '0');
    const endDateString = `${endYear}-${endMonth}-${endDay}`;
    
    const startDateObj = new Date();
    startDateObj.setDate(startDateObj.getDate() - 7);
    const startYear = startDateObj.getFullYear();
    const startMonth = String(startDateObj.getMonth() + 1).padStart(2, '0');
    const startDay = String(startDateObj.getDate()).padStart(2, '0');
    const startDateString = `${startYear}-${startMonth}-${startDay}`;
    
    setStartDate(startDateString);
    setEndDate(endDateString);
    setInsightsData(null);
    setAnalysisData(null);
    setError('');
    setAnalysisError('');
    setShowDatePicker(false);
    setSelectingStart(true);
    setCurrentMonth(new Date());
    setHoveredDate(null);
  };

  // Date range picker helpers
  const formatDateRange = () => {
    if (!startDate || !endDate) {
      return 'Select dates';
    }
    
    const parseDate = (dateString) => {
      const [year, month, day] = dateString.split('-');
      return new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
    };
    
    const startDateObj = parseDate(startDate);
    const endDateObj = parseDate(endDate);
    
    if (startDate === endDate) {
      return startDateObj.toLocaleDateString();
    }
    return `${startDateObj.toLocaleDateString()} - ${endDateObj.toLocaleDateString()}`;
  };

  const openDatePicker = () => {
    setSelectingStart(true);
    setShowDatePicker(true);
  };

  const formatDateToString = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const handleDateClick = (date) => {
    const dateString = formatDateToString(date);
    
    if (selectingStart) {
      setStartDate(dateString);
      setEndDate(dateString);
      setSelectingStart(false);
    } else {
      if (new Date(dateString) >= new Date(startDate)) {
        setEndDate(dateString);
        setShowDatePicker(false);
        setSelectingStart(true);
      } else {
        setStartDate(dateString);
        setEndDate(startDate);
        setShowDatePicker(false);
        setSelectingStart(true);
      }
    }
  };

  const setQuickRange = (days) => {
    const end = new Date();
    const start = new Date();
    start.setDate(end.getDate() - days);
    
    setStartDate(formatDateToString(start));
    setEndDate(formatDateToString(end));
    setShowDatePicker(false);
    setSelectingStart(true);
  };

  const isDateInRange = (date) => {
    if (!startDate || !endDate) return false;
    const dateStr = formatDateToString(date);
    return dateStr >= startDate && dateStr <= endDate;
  };

  const isDateRangeHover = (date) => {
    if (!hoveredDate || !startDate || selectingStart) return false;
    const dateStr = formatDateToString(date);
    const startStr = startDate;
    const hoverStr = formatDateToString(hoveredDate);
    
    if (hoverStr >= startStr) {
      return dateStr >= startStr && dateStr <= hoverStr;
    } else {
      return dateStr >= hoverStr && dateStr <= startStr;
    }
  };

  const isStartDate = (date) => {
    return startDate === formatDateToString(date);
  };

  const isEndDate = (date) => {
    return endDate === formatDateToString(date) && startDate !== endDate;
  };

  const getDaysInMonth = (date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDayOfWeek = firstDay.getDay();
    
    const days = [];
    
    for (let i = 0; i < startingDayOfWeek; i++) {
      days.push(null);
    }
    
    for (let day = 1; day <= daysInMonth; day++) {
      days.push(new Date(year, month, day));
    }
    
    return days;
  };

  const navigateMonth = (direction) => {
    setCurrentMonth(prev => {
      const newMonth = new Date(prev);
      newMonth.setMonth(prev.getMonth() + direction);
      return newMonth;
    });
  };

  // Format number with commas
  const formatNumber = (value) => {
    if (value === null || value === undefined) return '0';
    return value.toLocaleString();
  };

  // Format inline markdown (bold, italic, etc)
  const formatInlineMarkdown = (text) => {
    const parts = [];
    let currentIndex = 0;
    const regex = /(\*\*[^*]+\*\*|\*[^*]+\*|`[^`]+`)/g;
    let match;
    
    while ((match = regex.exec(text)) !== null) {
      // Add text before the match
      if (match.index > currentIndex) {
        parts.push(text.substring(currentIndex, match.index));
      }
      
      const matched = match[0];
      // Bold (**text**)
      if (matched.startsWith('**') && matched.endsWith('**')) {
        parts.push(
          <strong key={match.index} style={{ fontWeight: '600', color: '#333' }}>
            {matched.slice(2, -2)}
          </strong>
        );
      }
      // Italic (*text*)
      else if (matched.startsWith('*') && matched.endsWith('*')) {
        parts.push(
          <em key={match.index}>
            {matched.slice(1, -1)}
          </em>
        );
      }
      // Code (`text`)
      else if (matched.startsWith('`') && matched.endsWith('`')) {
        parts.push(
          <code key={match.index} style={{ 
            background: '#f1f5f9',
            padding: '2px 6px',
            borderRadius: '3px',
            fontSize: '0.9em',
            fontFamily: 'monospace'
          }}>
            {matched.slice(1, -1)}
          </code>
        );
      }
      
      currentIndex = match.index + matched.length;
    }
    
    // Add remaining text
    if (currentIndex < text.length) {
      parts.push(text.substring(currentIndex));
    }
    
    return parts.length > 0 ? parts : text;
  };

  // Format OpenAI response - simpler approach
  const formatAIResponse = (text) => {
    if (!text) return null;
    
    const lines = text.split('\n');
    const formatted = [];
    
    lines.forEach((line, index) => {
      const trimmed = line.trim();
      
      // Empty lines
      if (trimmed === '') {
        formatted.push(<div key={`space-${index}`} style={{ height: '12px' }} />);
        return;
      }
      
      // Section headers: Lines starting with "-" (with optional space) followed by uppercase, no colon
      // Examples: "-Friction & Root Causes", "- Risk / Compliance Flags", "-Agent Behavior Check"
      // Must be < 80 chars and not contain colons
      if (trimmed.match(/^-\s*[A-Z]/) && !trimmed.includes(':') && trimmed.length < 80) {
        const content = trimmed.replace(/^-\s*/, '');
        formatted.push(
          <h4 key={index} style={{ 
            marginTop: '24px', 
            marginBottom: '14px', 
            color: '#1f2937', 
            fontSize: '1.1em', 
            fontWeight: '700',
            borderLeft: '4px solid #4285f4',
            paddingLeft: '12px',
            lineHeight: '1.3'
          }}>
            {content}
          </h4>
        );
        return;
      }
      
      // Numbered lists (1. or 1) format)
      if (trimmed.match(/^\d+[.)]\s/)) {
        const content = trimmed.replace(/^\d+[.)]\s*/, '');
        const number = trimmed.match(/^(\d+)/)[1];
        formatted.push(
          <div key={index} style={{ 
            marginLeft: '24px', 
            marginBottom: '8px', 
            display: 'flex', 
            gap: '12px', 
            alignItems: 'flex-start' 
          }}>
            <span style={{ 
              minWidth: '28px', 
              fontWeight: '700', 
              color: '#4285f4', 
              fontSize: '0.95em' 
            }}>
              {number}.
            </span>
            <span style={{ flex: 1, lineHeight: '1.7' }}>{formatInlineMarkdown(content)}</span>
          </div>
        );
        return;
      }
      
      // Bullet points with colon (label: value format)
      if ((trimmed.startsWith('-') || trimmed.startsWith('*')) && trimmed.includes(':')) {
        const content = trimmed.replace(/^[-*]\s*/, '');
        const colonIndex = content.indexOf(':');
        const label = content.substring(0, colonIndex);
        const rest = content.substring(colonIndex + 1).trim();
        
        formatted.push(
          <div key={index} style={{ 
            marginLeft: '24px', 
            marginBottom: '10px', 
            display: 'flex', 
            gap: '12px', 
            alignItems: 'flex-start' 
          }}>
            <span style={{ 
              color: '#4285f4', 
              fontSize: '1.3em', 
              lineHeight: '1.5',
              marginTop: '-2px'
            }}>
              •
            </span>
            <div style={{ flex: 1, lineHeight: '1.7' }}>
              <strong style={{ fontWeight: '600', color: '#1f2937' }}>{label}:</strong>
              {rest && <span style={{ marginLeft: '6px' }}>{formatInlineMarkdown(rest)}</span>}
            </div>
          </div>
        );
        return;
      }
      
      // Regular bullet points
      if (trimmed.startsWith('-') || trimmed.startsWith('*')) {
        const content = trimmed.replace(/^[-*]\s*/, '');
        formatted.push(
          <div key={index} style={{ 
            marginLeft: '24px', 
            marginBottom: '8px', 
            display: 'flex', 
            gap: '12px', 
            alignItems: 'flex-start' 
          }}>
            <span style={{ 
              color: '#4285f4', 
              fontSize: '1.3em', 
              lineHeight: '1.5',
              marginTop: '-2px'
            }}>
              •
            </span>
            <span style={{ flex: 1, lineHeight: '1.7' }}>{formatInlineMarkdown(content)}</span>
          </div>
        );
        return;
      }
      
      // Regular paragraphs
      formatted.push(
        <p key={index} style={{ marginBottom: '12px', lineHeight: '1.7', color: '#374151' }}>
          {formatInlineMarkdown(line)}
        </p>
      );
    });
    
    return formatted;
  };

  // State for metric toggles
  const [activeMetrics, setActiveMetrics] = useState({
    totalCalls: true,
    successCount: true,
    potentialIssuesCount: true,
    callQualityIssueCount: true
  });
  
  const [hoveredPoint, setHoveredPoint] = useState(null);
  
  // State for hierarchical tree expansion
  const [expandedNodes, setExpandedNodes] = useState({});

  // Toggle metric visibility
  const toggleMetric = (metricKey) => {
    setActiveMetrics(prev => ({
      ...prev,
      [metricKey]: !prev[metricKey]
    }));
  };
  
  // Toggle tree node expansion
  const toggleNodeExpansion = (nodeId) => {
    setExpandedNodes(prev => ({
      ...prev,
      [nodeId]: !prev[nodeId]
    }));
  };

  // Optimized hover handlers
  const handleMouseEnter = (index, point) => {
    setHoveredPoint({ index, point });
  };

  const handleMouseLeave = () => {
    setHoveredPoint(null);
  };

  // Extract daily call data for timeline chart
  const extractTimelineData = (callsData) => {
    if (!callsData || callsData.length === 0) return [];
    
    // Group calls by date
    const callsByDate = {};
    callsData.forEach(call => {
      const callDate = new Date(call.callTs * 1000); // Convert unix timestamp to Date
      const dateStr = formatDateToString(callDate);
      
      if (!callsByDate[dateStr]) {
        callsByDate[dateStr] = {
          date: dateStr,
          totalCalls: 0,
          totalDuration: 0,
          callCount: 0,
          successCount: 0,
          potentialIssuesCount: 0,
          callQualityIssueCount: 0,
          issueCategories: {} // Track count by category
        };
      }
      
      callsByDate[dateStr].totalCalls += 1;
      callsByDate[dateStr].callCount += 1;
      
      if (call.duration) {
        callsByDate[dateStr].totalDuration += call.duration;
      }
      
      // Extract judge response metrics
      const judgeResponse = call.judgeResponse || {};
      
      // Count successful calls
      if (judgeResponse.success === true) {
        callsByDate[dateStr].successCount += 1;
      }
      
      // Count calls with potential issues and track categories
      if (judgeResponse.potential_issues && Array.isArray(judgeResponse.potential_issues) && judgeResponse.potential_issues.length > 0) {
        callsByDate[dateStr].potentialIssuesCount += 1;
        
        // Count each category of issue
        judgeResponse.potential_issues.forEach(issue => {
          const category = issue.category || 'unknown';
          if (!callsByDate[dateStr].issueCategories[category]) {
            callsByDate[dateStr].issueCategories[category] = 0;
          }
          callsByDate[dateStr].issueCategories[category] += 1;
        });
      }
      
      // Count calls with call quality issues
      if (judgeResponse.bCallQualityIssue === true) {
        callsByDate[dateStr].callQualityIssueCount += 1;
      }
    });
    
    // Convert to array and calculate averages
    return Object.values(callsByDate).map(dayData => ({
      date: dayData.date,
      totalCalls: dayData.totalCalls,
      avgDuration: dayData.callCount > 0 ? dayData.totalDuration / dayData.callCount : 0,
      successCount: dayData.successCount,
      potentialIssuesCount: dayData.potentialIssuesCount,
      callQualityIssueCount: dayData.callQualityIssueCount,
      issueCategories: dayData.issueCategories
    })).sort((a, b) => {
      const parseLocalDate = (dateStr) => {
        const [year, month, day] = dateStr.split('-');
        return new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
      };
      return parseLocalDate(a.date) - parseLocalDate(b.date);
    });
  };
  
  // Build hierarchical tree structure from call data
  const buildCallHierarchy = (callsData) => {
    if (!callsData || callsData.length === 0) return [];
    
    // Define the hierarchy structure
    const hierarchy = {};
    
    callsData.forEach(call => {
      const judgeResponse = call.judgeResponse || {};
      const tag = judgeResponse.tags || 'Unclassified';
      const outcome = judgeResponse.outcomes || '';
      const reason = judgeResponse.reasons || '';
      
      // Level 1: Tags
      if (!hierarchy[tag]) {
        hierarchy[tag] = {
          label: tag,
          count: 0,
          children: {}
        };
      }
      hierarchy[tag].count += 1;
      
      // Level 2: Outcomes (if exists)
      if (outcome) {
        if (!hierarchy[tag].children[outcome]) {
          hierarchy[tag].children[outcome] = {
            label: outcome,
            count: 0,
            children: {}
          };
        }
        hierarchy[tag].children[outcome].count += 1;
        
        // Level 3: Reasons (if exists)
        if (reason) {
          if (!hierarchy[tag].children[outcome].children[reason]) {
            hierarchy[tag].children[outcome].children[reason] = {
              label: reason,
              count: 0,
              children: {}
            };
          }
          hierarchy[tag].children[outcome].children[reason].count += 1;
        }
      }
    });
    
    // Convert to array format and sort by count
    const convertToArray = (obj) => {
      return Object.values(obj).sort((a, b) => b.count - a.count);
    };
    
    const result = convertToArray(hierarchy).map(level1 => ({
      ...level1,
      children: convertToArray(level1.children).map(level2 => ({
        ...level2,
        children: convertToArray(level2.children)
      }))
    }));
    
    return result;
  };
  
  // Category display names and colors for potential issues
  const issueCategoryConfig = {
    function_call: { label: 'Function Call Errors', color: '#e74c3c', icon: '⚙️' },
    instructions: { label: 'Instruction Violations', color: '#9b59b6', icon: '📋' },
    frustrated_user: { label: 'Frustrated Users', color: '#e67e22', icon: '😤' },
    transcription_errors: { label: 'Transcription Errors', color: '#3498db', icon: '🎤' },
    cancel_reschedule_policy: { label: 'Cancel/Reschedule Policy', color: '#1abc9c', icon: '📅' },
    premature_transfers: { label: 'Premature Transfers', color: '#f39c12', icon: '📞' },
    duplicate_booking: { label: 'Duplicate Bookings', color: '#e91e63', icon: '📝' }
  };
  
  // Build potential issues hierarchy from call data
  const buildIssuesHierarchy = (callsData) => {
    if (!callsData || callsData.length === 0) return { categories: [], totalIssues: 0, callsWithIssues: 0 };
    
    const issuesByCategory = {};
    let totalIssues = 0;
    let callsWithIssues = 0;
    
    callsData.forEach(call => {
      const judgeResponse = call.judgeResponse || {};
      const potentialIssues = judgeResponse.potential_issues;
      
      if (potentialIssues && Array.isArray(potentialIssues) && potentialIssues.length > 0) {
        callsWithIssues += 1;
        
        potentialIssues.forEach(issue => {
          const category = issue.category || 'unknown';
          const issueText = issue.issue || 'No description';
          totalIssues += 1;
          
          if (!issuesByCategory[category]) {
            issuesByCategory[category] = {
              category: category,
              label: issueCategoryConfig[category]?.label || category.replace(/_/g, ' '),
              color: issueCategoryConfig[category]?.color || '#6b7280',
              icon: issueCategoryConfig[category]?.icon || '❓',
              count: 0,
              issues: {}
            };
          }
          issuesByCategory[category].count += 1;
          
          // Group identical issues together
          if (!issuesByCategory[category].issues[issueText]) {
            issuesByCategory[category].issues[issueText] = {
              text: issueText,
              count: 0
            };
          }
          issuesByCategory[category].issues[issueText].count += 1;
        });
      }
    });
    
    // Convert to sorted arrays
    const categories = Object.values(issuesByCategory)
      .map(cat => ({
        ...cat,
        issues: Object.values(cat.issues).sort((a, b) => b.count - a.count)
      }))
      .sort((a, b) => b.count - a.count);
    
    return { categories, totalIssues, callsWithIssues };
  };
  
  // Render issue category node
  const renderIssueCategoryNode = (categoryNode, totalCalls) => {
    const nodeKey = `issue-${categoryNode.category}`;
    const isExpanded = expandedNodes[nodeKey];
    const hasIssues = categoryNode.issues && categoryNode.issues.length > 0;
    const percentage = totalCalls > 0 ? ((categoryNode.count / totalCalls) * 100).toFixed(1) : '0';
    
    return (
      <div key={nodeKey} style={{ marginBottom: '8px' }}>
        <div 
          style={{
            display: 'flex',
            alignItems: 'center',
            padding: '12px 16px',
            borderRadius: '8px',
            cursor: hasIssues ? 'pointer' : 'default',
            transition: 'all 0.2s',
            background: isExpanded ? '#fafafa' : 'white',
            border: `2px solid ${categoryNode.color}20`,
            borderLeft: `4px solid ${categoryNode.color}`,
            boxShadow: isExpanded ? '0 2px 8px rgba(0,0,0,0.08)' : '0 1px 3px rgba(0,0,0,0.05)'
          }}
          onClick={() => hasIssues && toggleNodeExpansion(nodeKey)}
          onMouseEnter={(e) => {
            if (hasIssues) {
              e.currentTarget.style.background = '#f5f5f5';
              e.currentTarget.style.boxShadow = '0 2px 8px rgba(0,0,0,0.1)';
            }
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.background = isExpanded ? '#fafafa' : 'white';
            e.currentTarget.style.boxShadow = isExpanded ? '0 2px 8px rgba(0,0,0,0.08)' : '0 1px 3px rgba(0,0,0,0.05)';
          }}
        >
          {hasIssues && (
            <span style={{ 
              marginRight: '12px', 
              fontSize: '0.8em',
              color: '#9ca3af',
              transition: 'transform 0.2s',
              transform: isExpanded ? 'rotate(90deg)' : 'rotate(0deg)',
              display: 'inline-block'
            }}>
              ▶
            </span>
          )}
          {!hasIssues && <span style={{ marginRight: '12px', width: '12px' }}></span>}
          
          <span style={{ marginRight: '10px', fontSize: '1.2em' }}>{categoryNode.icon}</span>
          
          <span style={{ 
            flex: 1, 
            fontWeight: '600', 
            color: '#1f2937',
            fontSize: '1em'
          }}>
            {categoryNode.label}
          </span>
          
          <span style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '10px'
          }}>
            <span style={{
              background: categoryNode.color,
              color: 'white',
              padding: '4px 12px',
              borderRadius: '16px',
              fontSize: '0.9em',
              fontWeight: '700',
              minWidth: '45px',
              textAlign: 'center'
            }}>
              {categoryNode.count}
            </span>
            <span style={{
              fontSize: '0.9em',
              color: '#6b7280',
              minWidth: '55px',
              textAlign: 'right'
            }}>
              {percentage}%
            </span>
          </span>
        </div>
        
        {hasIssues && isExpanded && (
          <div style={{ 
            marginLeft: '20px', 
            marginTop: '8px',
            borderLeft: `2px dashed ${categoryNode.color}40`,
            paddingLeft: '16px'
          }}>
            {categoryNode.issues.map((issue, idx) => (
              <div 
                key={`${nodeKey}-issue-${idx}`}
                style={{
                  padding: '10px 14px',
                  marginBottom: '6px',
                  background: '#f9fafb',
                  borderRadius: '6px',
                  fontSize: '0.9em',
                  color: '#4b5563',
                  lineHeight: '1.6',
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: '10px'
                }}
              >
                <span style={{
                  background: categoryNode.color + '20',
                  color: categoryNode.color,
                  padding: '2px 8px',
                  borderRadius: '10px',
                  fontSize: '0.8em',
                  fontWeight: '600',
                  flexShrink: 0
                }}>
                  ×{issue.count}
                </span>
                <span style={{ flex: 1 }}>{issue.text}</span>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  };
  
  // Render hierarchical tree node (recursive)
  const renderTreeNode = (node, level = 1, parentKey = '') => {
    const nodeKey = `${parentKey}-${node.label}`;
    const isExpanded = expandedNodes[nodeKey];
    const hasChildren = node.children && node.children.length > 0;
    const percentage = insightsData?.data ? ((node.count / insightsData.data.length) * 100).toFixed(1) : '0';
    
    // Color scheme by level
    const levelColors = {
      1: '#4285f4', // Blue
      2: '#34a853', // Green
      3: '#fbbc04'  // Yellow
    };
    
    const levelStyles = {
      1: {
        fontSize: '1.05em',
        fontWeight: '600',
        color: '#1f2937',
        marginBottom: '8px'
      },
      2: {
        fontSize: '0.95em',
        fontWeight: '500',
        color: '#374151',
        marginBottom: '6px'
      },
      3: {
        fontSize: '0.9em',
        fontWeight: '400',
        color: '#6b7280',
        marginBottom: '4px'
      }
    };
    
    return (
      <div key={nodeKey} style={{ marginLeft: level === 1 ? '0' : '24px' }}>
        <div 
          style={{
            display: 'flex',
            alignItems: 'center',
            padding: '8px 12px',
            borderRadius: '6px',
            cursor: hasChildren ? 'pointer' : 'default',
            transition: 'background 0.2s',
            background: isExpanded ? '#f9fafb' : 'transparent',
            borderLeft: `3px solid ${levelColors[level] || '#d1d5db'}`,
            marginBottom: '4px',
            ...levelStyles[level]
          }}
          onClick={() => hasChildren && toggleNodeExpansion(nodeKey)}
          onMouseEnter={(e) => hasChildren && (e.currentTarget.style.background = '#f3f4f6')}
          onMouseLeave={(e) => (e.currentTarget.style.background = isExpanded ? '#f9fafb' : 'transparent')}
        >
          {hasChildren && (
            <span style={{ 
              marginRight: '8px', 
              fontSize: '0.8em',
              color: '#9ca3af',
              transition: 'transform 0.2s',
              transform: isExpanded ? 'rotate(90deg)' : 'rotate(0deg)',
              display: 'inline-block'
            }}>
              ▶
            </span>
          )}
          {!hasChildren && <span style={{ marginRight: '8px', width: '12px' }}></span>}
          
          <span style={{ flex: 1 }}>{node.label}</span>
          
          <span style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '8px'
          }}>
            <span style={{
              background: levelColors[level] || '#d1d5db',
              color: 'white',
              padding: '2px 8px',
              borderRadius: '12px',
              fontSize: '0.85em',
              fontWeight: '600',
              minWidth: '40px',
              textAlign: 'center'
            }}>
              {node.count}
            </span>
            <span style={{
              fontSize: '0.85em',
              color: '#9ca3af',
              minWidth: '50px',
              textAlign: 'right'
            }}>
              {percentage}%
            </span>
          </span>
        </div>
        
        {hasChildren && isExpanded && (
          <div style={{ marginTop: '4px' }}>
            {node.children.map(child => renderTreeNode(child, level + 1, nodeKey))}
          </div>
        )}
      </div>
    );
  };

  // Google Search Console style timeline chart
  const renderTimelineChart = () => {
    if (!insightsData?.data || insightsData.data.length === 0) return null;
    
    const timelineData = extractTimelineData(insightsData.data);
    if (timelineData.length === 0) return null;
    
    // Define all available metrics
    const allMetrics = [
      { key: 'totalCalls', label: 'Total Calls', color: '#4285f4', type: 'count' },
      { key: 'successCount', label: 'Successful Calls', color: '#34a853', type: 'count' },
      { key: 'potentialIssuesCount', label: 'Calls with Issues', color: '#fbbc04', type: 'count' },
      { key: 'callQualityIssueCount', label: 'Call Quality Issues', color: '#ea4335', type: 'count' }
    ];
    
    // Get only active metrics
    const activeMetricsList = allMetrics.filter(metric => activeMetrics[metric.key]);
    
    // Calculate scales for each type
    const getScale = (type) => {
      const relevantMetrics = activeMetricsList.filter(m => m.type === type);
      if (relevantMetrics.length === 0) return { min: 0, max: 100 };
      
      let allValues = [];
      relevantMetrics.forEach(metric => {
        timelineData.forEach(point => {
          allValues.push(point[metric.key] || 0);
        });
      });
      
      const max = Math.max(...allValues);
      return { min: 0, max: max * 1.1 };
    };
    
    const countScale = getScale('count');
    
    // Chart dimensions - responsive
    const chartWidth = 800;
    const chartHeight = 300;
    const padding = { top: 20, right: 60, bottom: 50, left: 60 };
    const plotWidth = chartWidth - padding.left - padding.right;
    const plotHeight = chartHeight - padding.top - padding.bottom;
    
    // Create coordinate functions - handle single point case
    const xScale = (index) => {
      if (timelineData.length === 1) {
        return plotWidth / 2;
      }
      return (index / (timelineData.length - 1)) * plotWidth;
    };
    const yScale = (value, type) => {
      const scale = countScale;
      return plotHeight - ((value - scale.min) / (scale.max - scale.min)) * plotHeight;
    };
    
    // Generate path for a metric line
    const generatePath = (metric) => {
      if (timelineData.length === 1) {
        return '';
      }
      const points = timelineData.map((point, index) => {
        const x = xScale(index);
        const y = yScale(point[metric.key] || 0, metric.type);
        return `${x},${y}`;
      });
      return `M ${points.join(' L ')}`;
    };
    
    // Format date for display - parse as local time
    const formatDateShort = (dateStr) => {
      const [year, month, day] = dateStr.split('-');
      const date = new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
      return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    };
    
    // Format day of week for display - parse as local time
    const formatDayOfWeek = (dateStr) => {
      const [year, month, day] = dateStr.split('-');
      const date = new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
      return date.toLocaleDateString('en-US', { weekday: 'short' });
    };
    
    // Format value for display
    const formatValue = (value, type) => {
      return formatNumber(Math.round(value));
    };
    
    return (
      <div className="gsc-chart-section">
        <h3 className="section-title">Call Volume Over Time</h3>
        
        {/* Metric Toggle Buttons */}
        <div className="metric-toggles">
          {allMetrics.map(metric => (
            <button
              key={metric.key}
              className={`metric-toggle ${activeMetrics[metric.key] ? 'active' : ''}`}
              onClick={() => toggleMetric(metric.key)}
              style={{
                '--metric-color': metric.color
              }}
            >
              <span className="metric-indicator"></span>
              {metric.label}
            </button>
          ))}
        </div>
        
        {/* Chart Container */}
        <div className="gsc-chart-container">
          <svg 
            width="100%" 
            height={chartHeight}
            viewBox={`0 0 ${chartWidth} ${chartHeight}`}
            className="gsc-chart-svg"
            onMouseLeave={handleMouseLeave}
          >
            {/* Y-axis grid lines */}
            {[0, 0.25, 0.5, 0.75, 1].map(ratio => {
              const y = padding.top + ratio * plotHeight;
              return (
                <line
                  key={ratio}
                  x1={padding.left}
                  y1={y}
                  x2={padding.left + plotWidth}
                  y2={y}
                  stroke="#f0f0f0"
                  strokeWidth="1"
                />
              );
            })}
            
            {/* X-axis grid lines */}
            {timelineData.map((_, index) => {
              if (index % Math.max(1, Math.floor(timelineData.length / 6)) !== 0) return null;
              const x = padding.left + xScale(index);
              return (
                <line
                  key={index}
                  x1={x}
                  y1={padding.top}
                  x2={x}
                  y2={padding.top + plotHeight}
                  stroke="#f0f0f0"
                  strokeWidth="1"
                />
              );
            })}
            
            {/* Metric lines */}
            {activeMetricsList.map(metric => (
              <path
                key={metric.key}
                d={generatePath(metric)}
                fill="none"
                stroke={metric.color}
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                transform={`translate(${padding.left}, ${padding.top})`}
                className="metric-line"
              />
            ))}
            
            {/* Invisible hover areas */}
            {timelineData.map((point, index) => (
              <rect
                key={`hover-${index}`}
                x={padding.left + xScale(index) - 40}
                y={padding.top - 20}
                width="80"
                height={plotHeight + 40}
                fill="transparent"
                style={{ cursor: 'pointer' }}
                onMouseEnter={() => handleMouseEnter(index, point)}
                onMouseLeave={handleMouseLeave}
              />
            ))}
            
            {/* Data points */}
            {activeMetricsList.map(metric => 
              timelineData.map((point, index) => (
                <circle
                  key={`${metric.key}-${index}`}
                  cx={padding.left + xScale(index)}
                  cy={padding.top + yScale(point[metric.key] || 0, metric.type)}
                  r={timelineData.length === 1 ? "8" : "4"}
                  fill={metric.color}
                  stroke="white"
                  strokeWidth="2"
                  className="data-point"
                  style={{ pointerEvents: 'none' }}
                />
              ))
            )}
            
            {/* Hover line */}
            {hoveredPoint && (
              <line
                x1={padding.left + xScale(hoveredPoint.index)}
                y1={padding.top}
                x2={padding.left + xScale(hoveredPoint.index)}
                y2={padding.top + plotHeight}
                stroke="#666"
                strokeWidth="1"
                strokeDasharray="4,4"
                className="hover-line"
              />
            )}
            
            {/* X-axis labels */}
            {timelineData.map((point, index) => {
              if (index % Math.max(1, Math.floor(timelineData.length / 6)) !== 0) return null;
              return (
                <g key={index}>
                  <text
                    x={padding.left + xScale(index)}
                    y={chartHeight - 25}
                    textAnchor="middle"
                    fontSize="12"
                    fill="#666"
                  >
                    {formatDateShort(point.date)}
                  </text>
                  <text
                    x={padding.left + xScale(index)}
                    y={chartHeight - 10}
                    textAnchor="middle"
                    fontSize="11"
                    fill="#999"
                  >
                    {formatDayOfWeek(point.date)}
                  </text>
                </g>
              );
            })}
            
            {/* Y-axis labels - Count scale (left) */}
            {activeMetricsList.some(m => m.type === 'count') && [0, 0.25, 0.5, 0.75, 1].map(ratio => {
              const value = countScale.min + ratio * (countScale.max - countScale.min);
              const y = padding.top + (1 - ratio) * plotHeight;
              return (
                <text
                  key={`count-${ratio}`}
                  x={padding.left - 10}
                  y={y + 4}
                  textAnchor="end"
                  fontSize="12"
                  fill="#666"
                >
                  {Math.round(value)}
                </text>
              );
            })}
          </svg>
          
          {/* Tooltip */}
          {hoveredPoint && (
            <div 
              className="chart-tooltip" 
              style={{
                left: `${Math.min(Math.max((hoveredPoint.index / (timelineData.length - 1)) * 100, 10), 90)}%`
              }}
            >
              <div className="tooltip-date">
                {formatDayOfWeek(hoveredPoint.point.date)}, {formatDateShort(hoveredPoint.point.date)}
              </div>
              {activeMetricsList.map(metric => (
                <div key={metric.key} className="tooltip-metric">
                  <span className="tooltip-color" style={{ backgroundColor: metric.color }}></span>
                  <span className="tooltip-label">{metric.label}:</span>
                  <span className="tooltip-value">
                    {formatValue(hoveredPoint.point[metric.key] || 0, metric.type)}
                  </span>
                </div>
              ))}
              
              {/* Issue Categories Breakdown */}
              {hoveredPoint.point.issueCategories && Object.keys(hoveredPoint.point.issueCategories).length > 0 && (
                <div className="tooltip-section" style={{ 
                  marginTop: '12px', 
                  paddingTop: '12px', 
                  borderTop: '1px solid rgba(255,255,255,0.2)' 
                }}>
                  <div className="tooltip-section-title" style={{ 
                    fontSize: '11px', 
                    fontWeight: '600', 
                    marginBottom: '6px',
                    opacity: 0.85
                  }}>
                    Issue Categories:
                  </div>
                  {Object.entries(hoveredPoint.point.issueCategories)
                    .sort((a, b) => b[1] - a[1]) // Sort by count descending
                    .map(([category, count]) => (
                      <div key={category} className="tooltip-issue-category" style={{ 
                        display: 'flex', 
                        justifyContent: 'space-between',
                        fontSize: '11px',
                        padding: '3px 0',
                        opacity: 0.9
                      }}>
                        <span className="issue-category-label" style={{ marginRight: '12px' }}>
                          {category.replace(/_/g, ' ')}:
                        </span>
                        <span className="issue-category-count" style={{ fontWeight: '600' }}>
                          {count}
                        </span>
                      </div>
                    ))}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="tab-content">
      <h1>Call Insights</h1>
      
      {/* Search Form */}
      <div className="analytics-form">
        <h3>Get Call Insights</h3>
        <div className="form-row">
          <div className="form-group">
            <label htmlFor="practice-select">Practice:</label>
            {practicesLoading ? (
              <div className="loading-practices">Loading practices...</div>
            ) : practicesError ? (
              <div className="error-practices">{practicesError}</div>
            ) : (
              <select
                id="practice-select"
                value={practiceId}
                onChange={(e) => setPracticeId(e.target.value)}
                disabled={practices.length === 0}
              >
                <option value="ALL">ALL PRACTICES</option>
                {practices.map((practice) => (
                  <option key={practice.practiceId} value={practice.practiceId}>
                    {practice.practiceName} ({practice.practiceId})
                  </option>
                ))}
              </select>
            )}
          </div>
          
          <div className="form-group">
            <label>Date Range:</label>
            <div className="date-range-picker" ref={datePickerRef}>
              <button 
                type="button" 
                className="date-range-button"
                onClick={openDatePicker}
              >
                📅 {formatDateRange()}
                <span className="dropdown-arrow">▼</span>
              </button>
              
              {showDatePicker && (
                <div className="date-picker-dropdown">
                  <div className="date-picker-header">
                    <h4>
                      {selectingStart ? 'Select start date' : 'Select end date'}
                    </h4>
                    <button className="close-button" onClick={() => setShowDatePicker(false)}>✕</button>
                  </div>
                  
                  <div className="quick-ranges">
                    <button onClick={() => setQuickRange(0)} className="quick-range-btn">Today</button>
                    <button onClick={() => setQuickRange(6)} className="quick-range-btn">Last 7 days</button>
                    <button onClick={() => setQuickRange(29)} className="quick-range-btn">Last 30 days</button>
                    <button onClick={() => setQuickRange(89)} className="quick-range-btn">Last 90 days</button>
                  </div>
                  
                  <div className="calendar-container">
                    <div className="calendar-header">
                      <button className="nav-button" onClick={() => navigateMonth(-1)}>
                        ‹
                      </button>
                      <span className="month-year">
                        {currentMonth.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
                      </span>
                      <button className="nav-button" onClick={() => navigateMonth(1)}>
                        ›
                      </button>
                    </div>
                    
                    <div className="calendar-grid">
                      <div className="weekdays">
                        {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => (
                          <div key={day} className="weekday">{day}</div>
                        ))}
                      </div>
                      
                      <div className="days-grid">
                        {getDaysInMonth(currentMonth).map((date, index) => {
                          if (!date) {
                            return <div key={index} className="empty-day"></div>;
                          }
                          
                          const isToday = formatDateToString(date) === formatDateToString(new Date());
                          const isSelected = isStartDate(date) || isEndDate(date);
                          const isInRange = isDateInRange(date);
                          const isHovered = isDateRangeHover(date);
                          const isStart = isStartDate(date);
                          const isEnd = isEndDate(date);
                          
                          return (
                            <button
                              key={date.getDate()}
                              className={`calendar-day ${isToday ? 'today' : ''} ${isSelected ? 'selected' : ''} ${isInRange ? 'in-range' : ''} ${isHovered ? 'hovered' : ''} ${isStart ? 'start-date' : ''} ${isEnd ? 'end-date' : ''}`}
                              onClick={() => handleDateClick(date)}
                              onMouseEnter={() => setHoveredDate(date)}
                              onMouseLeave={() => setHoveredDate(null)}
                            >
                              {date.getDate()}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  </div>
                  
                  <div className="date-picker-footer">
                    <div className="selected-range">
                      {startDate && endDate ? (
                        <span>
                          {formatDateRange()}
                        </span>
                      ) : (
                        <span className="instruction">
                          {selectingStart ? 'Click to select start date' : 'Click to select end date'}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
        
        <div className="form-actions">
          <button onClick={fetchInsightsData} disabled={loading}>
            {loading ? 'Loading...' : 'Get Insights'}
          </button>
          <button onClick={clearData} disabled={loading}>
            Clear
          </button>
        </div>
      </div>
      
      {/* Error Message */}
      {error && (
        <div className="error-message">{error}</div>
      )}
      
      {/* Loading Indicator */}
      {loading && (
        <div className="loading-indicator">
          Loading call insights...
        </div>
      )}
      
      {/* Insights Data Display */}
      {!loading && insightsData && (
        <div className="analytics-dashboard">
          <div className="dashboard-header">
            <h2>Call Insights Dashboard</h2>
            <div className="header-meta">
              <span className="practice-id">Practice: {practiceId === 'ALL' ? 'ALL PRACTICES' : practiceId}</span>
              <span className="date-info">Date Range: {insightsData.date_range?.start_date} to {insightsData.date_range?.end_date}</span>
            </div>
          </div>
          
          {/* Summary KPI Cards */}
          {insightsData.data && (() => {
            // Calculate summary metrics from all calls
            const totalCalls = insightsData.data.length;
            const successCount = insightsData.data.filter(call => call.judgeResponse?.success === true).length;
            const potentialIssuesCount = insightsData.data.filter(call => 
              call.judgeResponse?.potential_issues && 
              Array.isArray(call.judgeResponse.potential_issues) && 
              call.judgeResponse.potential_issues.length > 0
            ).length;
            const callQualityIssueCount = insightsData.data.filter(call => 
              call.judgeResponse?.bCallQualityIssue === true
            ).length;
            
            return (
              <div className="analytics-summary-cards">
                <div className="summary-card primary">
                  <div className="card-icon">📞</div>
                  <div className="card-content">
                    <div className="card-title">Total Calls</div>
                    <div className="card-value">{totalCalls}</div>
                    <div className="card-subtitle">incoming calls analyzed</div>
                  </div>
                </div>
                
                <div className="summary-card success">
                  <div className="card-icon">✅</div>
                  <div className="card-content">
                    <div className="card-title">Successful Calls</div>
                    <div className="card-value">{successCount}</div>
                    <div className="card-subtitle">
                      {totalCalls > 0 ? `${Math.round((successCount / totalCalls) * 100)}%` : '0%'}
                    </div>
                  </div>
                </div>
                
                <div className="summary-card warning">
                  <div className="card-icon">⚠️</div>
                  <div className="card-content">
                    <div className="card-title">Calls with Issues</div>
                    <div className="card-value">{potentialIssuesCount}</div>
                    <div className="card-subtitle">
                      {totalCalls > 0 ? `${Math.round((potentialIssuesCount / totalCalls) * 100)}%` : '0%'}
                    </div>
                  </div>
                </div>
                
                <div className="summary-card danger">
                  <div className="card-icon">📡</div>
                  <div className="card-content">
                    <div className="card-title">Call Quality Issues</div>
                    <div className="card-value">{callQualityIssueCount}</div>
                    <div className="card-subtitle">
                      {totalCalls > 0 ? `${Math.round((callQualityIssueCount / totalCalls) * 100)}%` : '0%'}
                    </div>
                  </div>
                </div>
              </div>
            );
          })()}
          
          {/* Timeline Chart */}
          {renderTimelineChart()}
          
          {/* Call Classification Section - Hierarchical Tree */}
          {insightsData.data && (() => {
            const hierarchyData = buildCallHierarchy(insightsData.data);
            
            return (
              <div className="analytics-section">
                <div style={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  justifyContent: 'space-between',
                  marginBottom: '20px'
                }}>
                  <h3 className="section-title" style={{ margin: 0 }}>Why People Called</h3>
                  <div style={{ 
                    display: 'flex', 
                    gap: '20px',
                    fontSize: '0.85em',
                    color: '#6b7280'
                  }}>
                    <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                      <span style={{ 
                        width: '12px', 
                        height: '12px', 
                        background: '#4285f4',
                        borderRadius: '3px'
                      }}></span>
                      Call Type
                    </span>
                    <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                      <span style={{ 
                        width: '12px', 
                        height: '12px', 
                        background: '#34a853',
                        borderRadius: '3px'
                      }}></span>
                      Outcome
                    </span>
                    <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                      <span style={{ 
                        width: '12px', 
                        height: '12px', 
                        background: '#fbbc04',
                        borderRadius: '3px'
                      }}></span>
                      Reason
                    </span>
                  </div>
                </div>
                
                <div className="analytics-card" style={{ padding: '20px' }}>
                  {hierarchyData.length === 0 ? (
                    <div style={{ 
                      textAlign: 'center', 
                      padding: '40px',
                      color: '#9ca3af'
                    }}>
                      No classification data available
                    </div>
                  ) : (
                    <div style={{ 
                      maxHeight: '600px',
                      overflowY: 'auto',
                      paddingRight: '10px'
                    }}>
                      {hierarchyData.map(node => renderTreeNode(node))}
                    </div>
                  )}
                  
                  <div style={{
                    marginTop: '20px',
                    paddingTop: '15px',
                    borderTop: '1px solid #e5e7eb',
                    fontSize: '0.85em',
                    color: '#6b7280',
                    fontStyle: 'italic'
                  }}>
                    💡 Tip: Click on any category to expand and see sub-categories
                  </div>
                </div>
              </div>
            );
          })()}
          
          {/* Potential Issues Section */}
          {insightsData.data && (() => {
            const issuesData = buildIssuesHierarchy(insightsData.data);
            const totalCalls = insightsData.data.length;
            
            return (
              <div className="analytics-section">
                <div style={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  justifyContent: 'space-between',
                  marginBottom: '20px'
                }}>
                  <h3 className="section-title" style={{ margin: 0 }}>Potential Issues</h3>
                  <div style={{ 
                    display: 'flex', 
                    gap: '16px',
                    fontSize: '0.85em',
                    color: '#6b7280'
                  }}>
                    <span style={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      gap: '6px',
                      background: '#f3f4f6',
                      padding: '6px 12px',
                      borderRadius: '20px'
                    }}>
                      <span style={{ fontWeight: '600', color: '#374151' }}>{issuesData.callsWithIssues}</span>
                      calls with issues
                    </span>
                    <span style={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      gap: '6px',
                      background: '#fef3c7',
                      padding: '6px 12px',
                      borderRadius: '20px'
                    }}>
                      <span style={{ fontWeight: '600', color: '#92400e' }}>{issuesData.totalIssues}</span>
                      total issues
                    </span>
                  </div>
                </div>
                
                <div className="analytics-card" style={{ padding: '20px' }}>
                  {issuesData.categories.length === 0 ? (
                    <div style={{ 
                      textAlign: 'center', 
                      padding: '40px',
                      color: '#9ca3af'
                    }}>
                      <div style={{ fontSize: '3em', marginBottom: '12px' }}>🎉</div>
                      <div style={{ fontSize: '1.1em', fontWeight: '500' }}>No issues detected!</div>
                      <div style={{ fontSize: '0.9em', marginTop: '6px' }}>All calls in this period ran smoothly.</div>
                    </div>
                  ) : (
                    <div style={{ 
                      maxHeight: '600px',
                      overflowY: 'auto',
                      paddingRight: '10px'
                    }}>
                      {issuesData.categories.map(category => renderIssueCategoryNode(category, totalCalls))}
                    </div>
                  )}
                  
                  <div style={{
                    marginTop: '20px',
                    paddingTop: '15px',
                    borderTop: '1px solid #e5e7eb',
                    fontSize: '0.85em',
                    color: '#6b7280',
                    fontStyle: 'italic'
                  }}>
                    💡 Tip: Click on any category to see specific issue details
                  </div>
                </div>
              </div>
            );
          })()}
          
          {/* AI Analysis Section */}
          <div className="analytics-section">
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '15px' }}>
              <h3 className="section-title" style={{ margin: 0 }}>AI Analysis</h3>
              
              <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
                {/* Toggle Switch */}
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                  <span style={{ fontSize: '0.9em', opacity: 0.8 }}>Mode:</span>
                  <div style={{ display: 'flex', gap: '5px', background: '#f0f0f0', padding: '4px', borderRadius: '6px' }}>
                    <button
                      onClick={() => setAnalysisMode('judge')}
                      style={{
                        padding: '6px 12px',
                        border: 'none',
                        borderRadius: '4px',
                        background: analysisMode === 'judge' ? '#4285f4' : 'transparent',
                        color: analysisMode === 'judge' ? 'white' : '#666',
                        cursor: 'pointer',
                        fontSize: '0.9em',
                        fontWeight: analysisMode === 'judge' ? '600' : 'normal',
                        transition: 'all 0.2s'
                      }}
                    >
                      Judge
                    </button>
                    <button
                      onClick={() => setAnalysisMode('transcripts')}
                      style={{
                        padding: '6px 12px',
                        border: 'none',
                        borderRadius: '4px',
                        background: analysisMode === 'transcripts' ? '#4285f4' : 'transparent',
                        color: analysisMode === 'transcripts' ? 'white' : '#666',
                        cursor: 'pointer',
                        fontSize: '0.9em',
                        fontWeight: analysisMode === 'transcripts' ? '600' : 'normal',
                        transition: 'all 0.2s'
                      }}
                    >
                      Transcripts
                    </button>
                  </div>
                </div>
                
                {/* Get Analysis Button */}
                <button 
                  onClick={fetchAIAnalysis}
                  disabled={analysisLoading || !insightsData}
                  style={{
                    padding: '8px 16px',
                    background: insightsData ? '#4285f4' : '#ccc',
                    color: 'white',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: insightsData ? 'pointer' : 'not-allowed',
                    fontSize: '0.9em',
                    fontWeight: '500'
                  }}
                >
                  {analysisLoading ? 'Loading...' : 'Get Analysis'}
                </button>
              </div>
            </div>
            
            {analysisError && (
              <div className="error-message">{analysisError}</div>
            )}
            
            {!analysisLoading && analysisData && analysisData.openai_insights && (
              <div style={{ 
                maxWidth: '100%',
                background: 'white',
                padding: '25px',
                borderRadius: '8px',
                boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                border: '1px solid #e5e7eb'
              }}>
                <h4 style={{ 
                  marginBottom: '20px',
                  color: '#1f2937',
                  fontSize: '1.2em',
                  fontWeight: '600',
                  borderBottom: '2px solid #4285f4',
                  paddingBottom: '12px'
                }}>
                  AI-Generated Insights ({analysisData.mode === 'judge' ? 'Judge Analysis' : 'Transcript Analysis'})
                </h4>
                <div style={{ 
                  fontSize: '0.95em',
                  lineHeight: '1.8',
                  color: '#374151',
                  fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif'
                }}>
                  {formatAIResponse(analysisData.openai_insights)}
                </div>
              </div>
            )}
          </div>
        </div>
      )}
      
      {/* Instructions */}
      {!insightsData && !loading && !error && (
        <div className="instructions">
          <h3>Call Insights</h3>
          <p>Analyze incoming call patterns with judge response data.</p>
          
          <h4>How to use:</h4>
          <ol>
            <li>Select a practice from the dropdown</li>
            <li>Choose a date range (defaults to last 7 days)</li>
            <li>Click "Get Insights" to analyze calls</li>
          </ol>
          
          <p><strong>Note:</strong> Only includes incoming calls with judge analysis.</p>
        </div>
      )}
    </div>
  );
};

export default CallInsights;

